"use client";

import React from "react";
import { METRICS_EVENTS } from "@cede/types";
import { formatShortAddress } from "@cede/utils";
import { WalletButtonProps, DisconnectedButtonProps, ConnectedButtonProps, LoadingWrapperProps } from "./types";
import { AddressText } from "@/components/shared/AddressText/AddressText";
import { UserAvatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SkeletonElement } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { WALLET_STATUS } from "@/constants/wallet";
import { useHubUser } from "@/hooks";
import { useClipboard } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useOnboarding } from "@/hooks/onboarding";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Copy, LogOut, Wallet, User } from "lucide-react";
import { useRouter } from "next/navigation";

const buttonSizeHeightMap: Record<"small" | "medium" | "large", string> = {
  small: "h-9",
  medium: "h-[42px]",
  large: "h-[50px]",
};

const avatarSizeMap: Record<"small" | "medium" | "large", number> = {
  small: 24,
  medium: 28,
  large: 32,
};

const avatarSizeClassMap: Record<"small" | "medium" | "large", string> = {
  small: "w-6 h-6",
  medium: "w-7 h-7",
  large: "w-8 h-8",
};

const DisconnectedButton = ({ size, onClick, className, buttonText }: DisconnectedButtonProps) => {
  const { trackEvent } = useMetricsEvents();

  const onClickHandler = () => {
    // Track when user connects wallet
    trackEvent(METRICS_EVENTS.CONNECT_WALLET_OPEN, {});
    onClick();
  };

  return (
    <Button
      size={size}
      variant="secondary"
      onClick={onClickHandler}
      rightIcon={<Wallet className="size-5 text-cede-primary" strokeWidth={2} />}
      className={cn("min-w-[160px]", className)}
    >
      {buttonText ?? "Connect Wallet"}
    </Button>
  );
};

const ConnectedButton = ({
  address,
  size,
  className,
  onNavigateToProfile,
  onLogout,
  metricsSource,
}: ConnectedButtonProps) => {
  const [open, setOpen] = React.useState(false);
  const { isCopied, handleCopy } = useClipboard();
  const { trackEvent } = useMetricsEvents();
  const handleNavigate = React.useCallback(() => {
    setOpen(false);
    onNavigateToProfile();
    if (metricsSource) {
      trackEvent(METRICS_EVENTS.HEADER_WALLET_MENU_ITEM_CLICKED, { item: "profile", source: metricsSource });
    }
  }, [onNavigateToProfile, metricsSource, trackEvent]);

  const handleLogout = React.useCallback(() => {
    setOpen(false);
    if (metricsSource) {
      trackEvent(METRICS_EVENTS.HEADER_WALLET_MENU_ITEM_CLICKED, { item: "logout", source: metricsSource });
    }
    onLogout();
  }, [onLogout, metricsSource, trackEvent]);

  const handleCopyAddress = React.useCallback(async () => {
    await handleCopy(address);
    if (metricsSource) {
      trackEvent(METRICS_EVENTS.HEADER_WALLET_ADDRESS_COPIED, { source: metricsSource });
    }
  }, [address, handleCopy, metricsSource, trackEvent]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen && metricsSource) {
          trackEvent(METRICS_EVENTS.HEADER_WALLET_MENU_OPENED, { source: metricsSource });
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          size={size}
          variant="secondary"
          className={cn("min-w-[160px]", className)}
          leftIcon={
            <UserAvatar
              identifier={address}
              src="/avatar.png"
              alt="Profile avatar"
              size={avatarSizeMap[size]}
              className={avatarSizeClassMap[size]}
            />
          }
          rightIcon={
            <ChevronDown
              className={cn(
                "size-4 text-cede-primary transition-transform duration-200 ease-in-out",
                open ? "rotate-180" : "rotate-0",
              )}
            />
          }
        >
          <AddressText>{formatShortAddress(address)}</AddressText>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px] p-2.5">
        <div className="flex items-center gap-2 rounded-md px-2 py-1.5 bg-cede-bg-component/60">
          <UserAvatar identifier={address} src="/avatar.png" alt="Profile" size={34} className="w-9 h-9" />
          <div className="flex-1 min-w-0">
            <AddressText asChild className="text-sm font-semibold truncate">
              <span>{formatShortAddress(address)}</span>
            </AddressText>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-cede-text-secondary hover:text-cede-text-primary"
            onClick={handleCopyAddress}
            aria-label="Copy address"
          >
            {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <DropdownMenuItem
          className="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-base font-medium text-cede-text-primary"
          onSelect={handleNavigate}
        >
          <User className="size-5" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          variant="destructive"
          className="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-base font-medium"
          onSelect={handleLogout}
        >
          <LogOut className="size-5" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LoadingWrapper = ({ ready, children, className, size, buttonClassName }: LoadingWrapperProps) => {
  if (!ready) {
    return (
      <div className={cn(className, "inline-flex")}>
        <SkeletonElement className={cn("min-w-[160px] rounded-button", buttonSizeHeightMap[size], buttonClassName)} />
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};

export const WalletButtonWeb = ({ className, buttonClassName, size = "small", metricsSource }: WalletButtonProps) => {
  const { login, hubUser, authenticated, ready, userFriendlyDefiAddress, logout, isLoadingHubUser, walletStatus } =
    useHubUser();
  const { shouldShowOnboarding, openOnboarding } = useOnboarding();
  const router = useRouter();

  const handleNavigateToProfile = () => {
    router.push(ROUTES.PROFILE);
  };

  // Show skeleton if auth provider is not ready OR if user is authenticated but hubUser is still loading
  // This prevents "Connect Wallet" from flashing while loading user data
  const showSkeleton =
    !ready ||
    walletStatus === WALLET_STATUS.INITIALIZING ||
    (authenticated && isLoadingHubUser && !hubUser?.defiAddress);
  const shouldShowConnectedWallet = Boolean(authenticated && hubUser?.defiAddress);
  const handleWalletAction = () => {
    if (shouldShowOnboarding) {
      openOnboarding();
    } else {
      login();
    }
  };

  return (
    <LoadingWrapper ready={!showSkeleton} className={className} size={size} buttonClassName={buttonClassName}>
      {shouldShowConnectedWallet ? (
        <ConnectedButton
          address={userFriendlyDefiAddress}
          size={size}
          onNavigateToProfile={handleNavigateToProfile}
          onLogout={logout}
          className={buttonClassName}
          metricsSource={metricsSource}
        />
      ) : (
        <DisconnectedButton size={size} onClick={handleWalletAction} className={buttonClassName} />
      )}
    </LoadingWrapper>
  );
};
